import React from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { 
  FileText, 
  Download, 
  CheckSquare, 
  ClipboardList, 
  FileSpreadsheet,
  BookOpen,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import {
  generateSafetyInspectionChecklist,
  generateJobHazardAnalysisTemplate,
  generateEmergencyActionPlanTemplate,
  downloadDocument
} from '../data/documentTemplates';

const resources = [
  {
    id: 'safety-inspection',
    icon: CheckSquare,
    title: "Safety Inspection Checklist",
    description: "Comprehensive workplace safety inspection template",
    format: "PDF",
    size: "245 KB",
    free: true,
    generator: generateSafetyInspectionChecklist
  },
  {
    id: 'job-hazard-analysis',
    icon: ClipboardList,
    title: "Job Hazard Analysis Template",
    description: "Step-by-step JHA form for any job task",
    format: "PDF",
    size: "180 KB",
    free: true,
    generator: generateJobHazardAnalysisTemplate
  },
  {
    id: 'emergency-action-plan',
    icon: FileText,
    title: "Emergency Action Plan Template",
    description: "Customizable EAP document for your workplace",
    format: "PDF",
    size: "312 KB",
    free: true,
    generator: generateEmergencyActionPlanTemplate
  },
  {
    id: 'osha-300',
    icon: FileSpreadsheet,
    title: "OSHA 300 Log Template",
    description: "Injury and illness recordkeeping form",
    format: "Excel",
    size: "89 KB",
    free: false,
    generator: null
  },
  {
    id: 'ppe-guide',
    icon: BookOpen,
    title: "PPE Selection Guide",
    description: "Complete guide to choosing the right PPE",
    format: "PDF",
    size: "1.2 MB",
    free: false,
    generator: null
  },
  {
    id: 'incident-form',
    icon: ClipboardList,
    title: "Incident Investigation Form",
    description: "Root cause analysis investigation template",
    format: "PDF",
    size: "156 KB",
    free: false,
    generator: null
  }
];

const DownloadableResources = ({ onEnroll, isEnrolled }) => {
  const handleDownload = (resource) => {
    if (!resource.free && !isEnrolled) {
      onEnroll && onEnroll();
      return;
    }

    // Check if resource has a generator function
    if (resource.generator) {
      try {
        downloadDocument(resource.title, resource.generator);
        toast.success(`Downloaded: ${resource.title}`);
      } catch (error) {
        console.error('Download error:', error);
        toast.error('Failed to download. Please try again.');
      }
    } else {
      // For premium resources that don't have generators yet
      toast.info(`${resource.title} will be available soon!`);
    }
  };

  return (
    <section className="py-20 bg-slate-900" id="resources">
      <div className="container mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-12">
          <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Bonus Resources</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-4">
            Downloadable Templates & Guides
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Get practical tools to implement what you learn in your workplace
          </p>
        </div>

        {/* Resources grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {resources.map((resource) => (
            <Card 
              key={resource.id}
              className="bg-slate-800 border-slate-700 p-5 hover:border-emerald-500/50 transition-all group"
              data-testid={`resource-card-${resource.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                  <resource.icon className="w-5 h-5 text-emerald-400" />
                </div>
                {!resource.free && (
                  <span className="flex items-center gap-1 text-xs text-amber-400 bg-amber-400/10 px-2 py-1 rounded">
                    <Lock className="w-3 h-3" />
                    Enrolled
                  </span>
                )}
                {resource.free && (
                  <span className="text-xs text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded">
                    Free
                  </span>
                )}
              </div>
              
              <h3 className="font-semibold text-white mb-1">{resource.title}</h3>
              <p className="text-slate-400 text-sm mb-3">{resource.description}</p>
              
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">
                  {resource.format} • {resource.size}
                </span>
                <Button
                  size="sm"
                  variant={resource.free || isEnrolled ? "default" : "outline"}
                  className={resource.free || isEnrolled 
                    ? "bg-emerald-500 hover:bg-emerald-600 text-white" 
                    : "border-slate-600 text-slate-300 hover:bg-slate-700"
                  }
                  onClick={() => handleDownload(resource)}
                  data-testid={`download-btn-${resource.id}`}
                >
                  <Download className="w-4 h-4 mr-1" />
                  {resource.free || isEnrolled ? 'Download' : 'Unlock'}
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* Enroll CTA */}
        {!isEnrolled && (
          <div className="text-center mt-10">
            <p className="text-slate-400 mb-4">Enroll in the free course to unlock all resources</p>
            <Button
              onClick={onEnroll}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3"
              data-testid="enroll-resources-btn"
            >
              Enroll Free & Get All Resources
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default DownloadableResources;
